import { parseSearchWith, stringifySearchWith } from "@tanstack/react-router";

/**
 * JSON-decode a value only when it is an array or object this app wrote.
 * The decoder has already turned canonical numbers and `true`/`false` into
 * scalars, so anything left is text. Decoding `1e3`, `null` or `"x"` from it
 * would change what was typed: a view's string param `1e3` would come back as
 * `1000`, and `null` would vanish. Throwing keeps the raw string.
 */
function parseStructuredValue(raw: string): unknown {
	const value: unknown = JSON.parse(raw);
	if (
		value === null ||
		typeof value !== "object" ||
		JSON.stringify(value) !== raw
	) {
		throw new Error("not a structured search value");
	}
	return value;
}

/**
 * Keep primitive search params human-readable (`entityIds=62,147`) while still
 * JSON-serializing structured values like arrays and objects.
 */
export const parseRouterSearch = parseSearchWith(parseStructuredValue);
export const stringifyRouterSearch = stringifySearchWith(JSON.stringify);

/**
 * TSR treats `{key: undefined}` as different from `{}` when diffing search state,
 * because the URL stringifier drops undefined keys but `validateSearch` keeps
 * returning them. That mismatch causes an infinite commit loop inside
 * `Transitioner` on initial mount (React bails after ~150 setStates with
 * "Maximum update depth exceeded"). Always strip undefined values from the
 * object returned by `validateSearch`.
 */
export function pruneSearch<T extends Record<string, unknown>>(
	obj: T,
): Partial<T> {
	const result: Partial<T> = {};
	for (const key of Object.keys(obj) as Array<keyof T>) {
		const value = obj[key];
		if (value !== undefined) {
			result[key] = value;
		}
	}
	return result;
}
